import { useState, useEffect } from 'react'
import { collection, query, where, onSnapshot, orderBy, QueryConstraint } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { Booking } from '@/types'

interface UseBookingsOptions {
    orgId?: string
    userId?: string
    status?: string
    orderByField?: string
    orderDirection?: 'asc' | 'desc'
}

export function useBookings(options: UseBookingsOptions = {}) {
    const [bookings, setBookings] = useState<Booking[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<Error | null>(null)

    useEffect(() => {
        const constraints: QueryConstraint[] = []

        if (options.orgId) {
            constraints.push(where('orgId', '==', options.orgId))
        }

        if (options.userId) {
            constraints.push(where('userId', '==', options.userId))
        }

        if (options.status) {
            constraints.push(where('status', '==', options.status))
        }

        if (options.orderByField) {
            constraints.push(orderBy(options.orderByField, options.orderDirection || 'desc'))
        }

        if (constraints.length === 0) {
            setBookings([])
            setLoading(false)
            return
        }

        setLoading(true)
        const q = query(collection(db, 'bookings'), ...constraints)

        const unsubscribe = onSnapshot(
            q,
            (snapshot) => {
                const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Booking))
                setBookings(data)
                setLoading(false)
                setError(null)
            },
            (err) => {
                console.error('Error fetching bookings:', err)
                setError(err as Error)
                setLoading(false)
            }
        )

        return () => unsubscribe()
    }, [options.orgId, options.userId, options.status, options.orderByField, options.orderDirection])

    return { bookings, loading, error }
}
